/**
 * Public Workspace Creation Route
 * 
 * This route allows anonymous users to fill workspace data before authentication.
 * After form submission, users are prompted to create an account.
 * Draft data is saved to localStorage and restored after authentication.
 */

import { useEffect } from 'react';
import { Form, useActionData } from '@remix-run/react';
import { json, redirect, type ActionFunctionArgs, type LoaderFunctionArgs } from '@remix-run/node';
import { authenticateUser } from '~/.server/utils/auth';
import { getFormProps, getInputProps, getSelectProps, getTextareaProps, useForm, useInputControl, FormProvider } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { AuthenticityTokenInput } from 'remix-utils/csrf/react';
import { validateCSRF } from '~/.server/utils/security/csrf';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Button } from '~/components/ui/button';
import { Field, SelectField, TextareaField, CheckboxFieldArray } from '~/components/forms';
import { GoogleMapsProvider } from '~/components/googleMapsProvider';
import PlaceAutocomplete from '~/components/placeAutocomplete';
import { PhoneCountrySelect } from '~/components/phoneCountrySelect';
import { WorkspaceRentalTypesField } from '~/components/workspaceRentalTypeField';
import { useIsPending } from '~/utils/misc';
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert';
import { InfoIcon, CheckCircle2 } from 'lucide-react';
import { AuthenticationDialog, useAuthDialog } from '~/components/authenticationDialog';
import { saveWorkspaceDraft, workspaceDraftSchema, hasDraft, formatDraftAge } from '~/utils/workspaceDraft';
import { useQuery } from '@tanstack/react-query';
import { CategoryItem } from '~/.server/api/routers/workspace';
import { trackWorkspaceFormSubmitted } from '~/utils/analytics/workspaceConversion';
// Public schema - same as workspace schema but without ID and images
const publicWorkspaceSchema = workspaceDraftSchema.omit({
  pendingImages: true,
});

export async function loader({ request }: LoaderFunctionArgs) {
  // Check if user is already authenticated
  const { user, headers } = await authenticateUser(request);
  
  // If user is authenticated, redirect to the authenticated workspace creation route
  if (user) {
    throw redirect('/cuenta/espacios', { headers });
  }
  
  // Public route - allow anonymous users
  return json({}, { headers });
}

export async function action({ request }: ActionFunctionArgs) {
  if (request.method !== 'POST') {
    return json({ error: 'Method not allowed' }, { status: 405 });
  }

  const formData = await request.formData();
  await validateCSRF(formData, request.headers);

  const submission = parseWithZod(formData, { schema: publicWorkspaceSchema });

  if (submission.status !== 'success') {
    return submission.reply();
  }

  // Return success - client will handle saving to localStorage
  return json({ 
    success: true,
    data: submission.value,
  });
}

export default function CrearEspacioPublico() {
  const { data: categories } = useQuery<CategoryItem[]>({ queryKey: ['categories'] });
  const { data: amenities } = useQuery<CategoryItem[]>({ queryKey: ['amenities'] });
  const { data: rentalTypes } = useQuery<CategoryItem[]>({ queryKey: ['rentalTypes'] });
  const actionData = useActionData<typeof action>();
  const authDialog = useAuthDialog('/cuenta/espacios/nuevo');
  const isPending = useIsPending();

  const [form, fields] = useForm({
    id: 'public_workspace_form',
    constraint: getZodConstraint(publicWorkspaceSchema),
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: publicWorkspaceSchema });
    },
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
  });

  // Handle successful form submission
  useEffect(() => {
    if (actionData && 'success' in actionData && actionData.success) {
      // Track form submission
      trackWorkspaceFormSubmitted(actionData.data.category);
      
      // Save to localStorage
      saveWorkspaceDraft(actionData.data);
      
      // Show authentication dialog
      authDialog.open({
        redirectTo: '/cuenta/espacios/nuevo',
        defaultTab: 'signup',
      });
    }
  }, [actionData]);

  // Place autocomplete handlers
  const addressInput = useInputControl(fields.address);
  const cityInput = useInputControl(fields.city);
  const postalCodeInput = useInputControl(fields.postalCode);
  const countryCodeInput = useInputControl(fields.countryCode);
  const latitudeInput = useInputControl(fields.latitude);
  const longitudeInput = useInputControl(fields.longitude);

  function handlePlaceSelect(place: google.maps.places.PlaceResult) {
    addressInput.change(place.formatted_address || '');
    cityInput.change(place.address_components?.find(c => c.types.includes('locality'))?.long_name || '');
    postalCodeInput.change(place.address_components?.find(c => c.types.includes('postal_code'))?.long_name || '');
    countryCodeInput.change(place.address_components?.find(c => c.types.includes('country'))?.short_name || '');
    latitudeInput.change(place.geometry?.location?.lat().toString() || '');
    longitudeInput.change(place.geometry?.location?.lng().toString() || '');
  }

  // Check if there's an existing draft
  const existingDraft = hasDraft();
  const draftAge = formatDraftAge();

  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle className="text-3xl">Publica tu espacio</CardTitle>
          <CardDescription className="text-base">
            Completa la información de tu espacio. Después de este paso, crearemos tu cuenta para publicar el anuncio.
          </CardDescription>
          
          {/* Draft restoration alert */}
          {existingDraft && draftAge && (
            <Alert className="mt-4 border-blue-500 bg-blue-50">
              <InfoIcon className="h-4 w-4 text-blue-600" />
              <AlertTitle className="text-blue-900">
                Borrador guardado
              </AlertTitle>
              <AlertDescription className="text-blue-800">
                Tienes un borrador guardado {draftAge}. Puedes continuar editándolo.
              </AlertDescription>
            </Alert>
          )}

          {/* Info alert */}
          {/* <Alert className="mt-4">
            <InfoIcon className="h-4 w-4" />
            <AlertTitle>Sin compromiso</AlertTitle>
            <AlertDescription>
              Solo necesitamos tu email al final para crear tu cuenta. Podrás añadir fotos después de registrarte.
            </AlertDescription>
          </Alert> */}
        </CardHeader>

        <CardContent>
          <GoogleMapsProvider>
            <FormProvider context={form.context}>
              <Form method="post" {...getFormProps(form)} className="space-y-8">
                <AuthenticityTokenInput />

                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Información básica</h3>
                  
                  <Field
                    labelProps={{ htmlFor: fields.title.id, children: 'Título del espacio' }}
                    inputProps={{
                      ...getInputProps(fields.title, { type: 'text' }),
                      placeholder: 'Ej: Oficina luminosa en el centro',
                    }}
                    errors={fields.title.errors}
                  />

                  <TextareaField
                    labelProps={{ htmlFor: fields.content.id, children: 'Descripción' }}
                    textareaProps={{
                      ...getTextareaProps(fields.content),
                      className: 'min-h-32',
                      placeholder: 'Describe tu espacio: características, servicios incluidos, ambiente...',
                    }}
                    errors={fields.content.errors}
                  />

                  <SelectField
                    labelProps={{ htmlFor: fields.category.id, children: 'Categoría principal' }}
                    selectProps={{
                      ...getSelectProps(fields.category),
                      defaultValue: fields.category.value,
                    }}
                    options={categories || []}
                    errors={fields.category.errors}
                  />
                </div>

                {/* Location */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Ubicación</h3>
                  
                  <PlaceAutocomplete
                    onPlaceSelect={handlePlaceSelect}
                    name="location"
                    title="Dirección"
                    placeholder="Busca la dirección del espacio"
                  />

                

                  <input {...getInputProps(fields.city, { type: 'hidden' })} />
                  <input {...getInputProps(fields.countryCode, { type: 'hidden' })} />

                  <Field
                    labelProps={{ htmlFor: fields.addressLine2.id, children: 'Información adicional (opcional)' }}
                    inputProps={{
                      ...getInputProps(fields.addressLine2, { type: 'text' }),
                      placeholder: 'Piso, puerta, etc.',
                    }}
                    errors={fields.addressLine2.errors}
                  />

                  <Field
                    labelProps={{ htmlFor: fields.postalCode.id, children: 'Código postal' }}
                    inputProps={{
                      ...getInputProps(fields.postalCode, { type: 'text' }),
                      placeholder: '08001',
                    }}
                    errors={fields.postalCode.errors}
                  />
                    <div className="grid grid-cols-2 gap-4">
                    <Field
                      labelProps={{ htmlFor: fields.latitude.id, children: 'Latitud' }}
                      inputProps={{
                        ...getInputProps(fields.latitude, { type: 'text' }),
                        placeholder: '40.416775',
                        readOnly: true,
                      }}
                      errors={fields.latitude.errors}
                    />
                    <Field
                      labelProps={{ htmlFor: fields.longitude.id, children: 'Longitud' }}
                      inputProps={{
                        ...getInputProps(fields.longitude, { type: 'text' }),
                        placeholder: '-3.703790',
                        readOnly: true,
                      }}
                      errors={fields.longitude.errors}
                    />
                  </div>
                </div>

                {/* Contact */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Información de contacto</h3>
                  
                  <div className="flex gap-2">
                    <PhoneCountrySelect
                      labelProps={{ htmlFor: fields.phoneCountryCode.id, children: 'País' }}
                      selectProps={{
                        ...getSelectProps(fields.phoneCountryCode),
                        form: form.id,
                        defaultValue: '+34-ES',
                      }}
                      errors={fields.phoneCountryCode.errors}
                      language="es"
                    />
                    <div className="flex-1">
                      <Field
                        labelProps={{ htmlFor: fields.phoneNumber.id, children: 'Teléfono' }}
                        inputProps={{
                          ...getInputProps(fields.phoneNumber, { type: 'tel' }),
                          placeholder: '600 000 000',
                        }}
                        errors={fields.phoneNumber.errors}
                      />
                    </div>
                  </div>
                </div>

                {/* Pricing */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Precios</h3>
                  
                  <WorkspaceRentalTypesField
                    name={fields.rentalTypes.name}
                    rentalTypeOptions={rentalTypes || []}
                  />
                </div>

                {/* Amenities */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Servicios y comodidades</h3>
                  
                  <CheckboxFieldArray
                    labelProps={{ children: 'Selecciona los servicios disponibles' }}
                    buttonProps={{
                      name: fields.amenities.name,
                      form: form.id,
                    }}
                    options={amenities || []}
                    errors={fields.amenities.errors}
                  />
                </div>

                {/* Submit */}
                <div className="space-y-4 pt-6 border-t">
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertTitle>Siguiente paso</AlertTitle>
                    <AlertDescription>
                      Al hacer clic en continuar, te pediremos crear una cuenta para publicar tu espacio.
                      Podrás añadir fotos después.
                    </AlertDescription>
                  </Alert>

                  <Button type="submit" size="lg" className="w-full" disabled={isPending}>
                    {isPending ? 'Guardando...' : 'Continuar con el registro →'}
                  </Button>
                </div>
              </Form>
            </FormProvider>
          </GoogleMapsProvider>
        </CardContent>
      </Card>

      {/* Authentication Dialog */}
      <AuthenticationDialog
        open={authDialog.isOpen}
        onClose={authDialog.close}
        redirectTo={authDialog.redirectTo}
        defaultTab={authDialog.defaultTab}
        title="¡Último paso!"
        description="Crea tu cuenta para publicar tu espacio. Es gratis y solo toma un minuto."
      />
    </div>
  );
}
